import { useState } from 'react'
import { Clock, Activity } from 'lucide-react'
import { AttendanceFilters } from '../components/attendance/AttendanceFilters'
import { AttendanceTable } from '../components/attendance/AttendanceTable'
import { AttendanceEventDetails } from '../components/attendance/AttendanceEventDetails'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Pagination,
  Select,
  SelectOption,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { useAttendanceEvents } from '../hooks/useAttendance'
import { useEmployees } from '../hooks/useEmployees'
import { useLocations } from '../hooks/locations/useLocations'
import type { AttendanceEvent, AttendanceEventFilters } from '../types/attendance.types'

const PAGE_SIZES = [20, 50, 100, 200]

export function Attendance() {
  const [filters, setFilters] = useState<AttendanceEventFilters>({
    page: 1,
    limit: 20,
    sort: 'DESC',
  })

  const [selectedEvent, setSelectedEvent] = useState<AttendanceEvent | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)

  const { data, isLoading, error, refetch } = useAttendanceEvents(filters)
  const { data: employees } = useEmployees({ limit: 1000 })
  const { data: locations } = useLocations({ limit: 1000 })

  const handleFiltersChange = (newFilters: Partial<AttendanceEventFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }))
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleLimitChange = (limit: number) => {
    setFilters((prev) => ({ ...prev, limit, page: 1 }))
  }

  const handleViewDetails = (event: AttendanceEvent) => {
    setSelectedEvent(event)
    setShowDetailsModal(true)
  }

  const handleCloseDetails = () => {
    setShowDetailsModal(false)
    setSelectedEvent(null)
  }

  // Сводка приходит с бэкенда по всему отфильтрованному набору (все страницы).
  // Приходы/уходы — уникальные пары «сотрудник + день», а не число отметок
  const rangeStart = data && data.total > 0 ? (data.page - 1) * data.limit + 1 : 0
  const rangeEnd = data ? Math.min(data.page * data.limit, data.total) : 0

  const stats = data?.summary ? { total: data.total, ...data.summary } : null

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">События посещаемости</h1>
          <p className="text-muted-foreground">
            Мониторинг и управление событиями прихода/ухода сотрудников
          </p>
        </div>
        <ErrorState title="Ошибка загрузки событий посещаемости" onAction={refetch} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">События посещаемости</h1>
        <p className="text-muted-foreground">
          Мониторинг и управление событиями прихода/ухода сотрудников
        </p>
      </div>

      {/* Filters */}
      <AttendanceFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        employees={employees}
        locations={locations}
      />

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Всего событий</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.checkIns}</p>
                  <p className="text-sm text-muted-foreground">Приходов</p>
                  <p className="text-xs text-muted-foreground">по сотрудникам за день</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-red-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.checkOuts}</p>
                  <p className="text-sm text-muted-foreground">Уходов</p>
                  <p className="text-xs text-muted-foreground">по сотрудникам за день</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Список событий</CardTitle>
          <CardDescription>
            {data && `Показано ${rangeStart}–${rangeEnd} из ${data.total} событий`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <AttendanceTable events={data.data} onViewDetails={handleViewDetails} />

              {/* Pagination */}
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>На странице:</span>
                  <Select
                    value={String(filters.limit || 20)}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                    className="h-8 w-20"
                  >
                    {PAGE_SIZES.map((size) => (
                      <SelectOption key={size} value={String(size)}>
                        {size}
                      </SelectOption>
                    ))}
                  </Select>
                  <span>
                    {rangeStart}–{rangeEnd} из {data.total}
                  </span>
                </div>
                <Pagination
                  currentPage={data.page}
                  totalPages={data.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                События не найдены
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Попробуйте изменить фильтры поиска или выберите другой период.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Details Modal */}
      <AttendanceEventDetails
        event={selectedEvent}
        open={showDetailsModal}
        onClose={handleCloseDetails}
      />
    </div>
  )
}